import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { UserCheck, ChevronDown, ChevronUp, Check } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "fm_crew_shift";

/**
 * Selector de tripulación al inicio de turno.
 * Solo aparece para logins de rol "ambulancia" (tablet de la unidad).
 * Guarda en localStorage el paramédico/operador/médico que está a bordo.
 * Esos nombres se inyectan en firmas de FRAP, pings GPS y logs de auditoría.
 *
 * También llama a PUT /units/{unit_id} para registrar quién está a bordo
 * para que cabina lo vea en el mapa en vivo y en el dashboard.
 */
export default function ShiftCard({ unitId, unitLabel, onChange }) {
    const [personnel, setPersonnel] = useState([]);
    const [shift, setShift] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : { paramedic_id: "", driver_id: "", medic_id: "" };
        } catch { return { paramedic_id: "", driver_id: "", medic_id: "" }; }
    });
    const [expanded, setExpanded] = useState(!shift.paramedic_id && !shift.driver_id);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/personnel");
                setPersonnel(r.data || []);
            } catch { /* silent */ }
        })();
    }, []);

    const persById = (id) => personnel.find(p => p.id === id);
    const paraName = persById(shift.paramedic_id)?.name;
    const drvName = persById(shift.driver_id)?.name;
    const medName = persById(shift.medic_id)?.name;

    const activePersonnel = personnel.filter(p => p.status === "activo");
    const paramedics = activePersonnel.filter(p => ["paramedico", "medico", "enfermera"].includes(p.position));
    const drivers = activePersonnel.filter(p => p.position === "operador");
    const medics = activePersonnel.filter(p => ["medico", "enfermera"].includes(p.position));

    const save = async () => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(shift)); } catch (e) { void e; }
        // Persistir en la unidad para que cabina lo vea
        if (unitId) {
            try {
                // Cargar unidad actual primero
                const r = await api.get(`/units/${unitId}`);
                const u = r?.data?.unit || {};
                await api.put(`/units/${unitId}`, {
                    ...u,
                    current_paramedic_id: shift.paramedic_id || null,
                    current_driver_id: shift.driver_id || null,
                    current_medic_id: shift.medic_id || null,
                });
            } catch (e) { console.warn("No se pudo actualizar la unidad:", e); }
        }
        toast.success("Tripulación registrada — listos para el turno");
        setExpanded(false);
        if (onChange) onChange(shift);
    };

    const clear = () => {
        const empty = { paramedic_id: "", driver_id: "", medic_id: "" };
        setShift(empty);
        try { localStorage.removeItem(STORAGE_KEY); } catch (e) { void e; }
        toast("Tripulación borrada");
    };

    const ready = !!(shift.paramedic_id || shift.driver_id);

    return (
        <div className="fm-card border-l-4 border-l-emerald-500" data-testid="shift-card">
            <button
                type="button"
                onClick={() => setExpanded(e => !e)}
                className="w-full flex items-center justify-between p-3 hover:bg-slate-50"
                data-testid="shift-toggle"
            >
                <div className="flex items-center gap-2 text-left">
                    <UserCheck className={`w-5 h-5 ${ready ? "text-emerald-600" : "text-amber-500"}`} />
                    <div>
                        <div className="font-bold text-sm">
                            {ready ? "Tripulación a bordo" : "⚠️ Identifica tu tripulación"}
                        </div>
                        <div className="text-[11px] text-slate-500 leading-tight">
                            {ready ? (
                                <>
                                    {paraName && <span>Paramédico: <strong>{paraName}</strong></span>}
                                    {paraName && drvName && " · "}
                                    {drvName && <span>Operador: <strong>{drvName}</strong></span>}
                                    {medName && <> · Médico: <strong>{medName}</strong></>}
                                </>
                            ) : "Para que tu nombre aparezca en FRAP, firmas y logs"}
                        </div>
                        {unitLabel && <div className="text-[10px] font-mono text-slate-400 mt-0.5">🚑 {unitLabel}</div>}
                    </div>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>

            {expanded && (
                <div className="border-t border-slate-200 p-3 space-y-2">
                    <div>
                        <label className="fm-label">Paramédico a cargo</label>
                        <select
                            value={shift.paramedic_id}
                            onChange={(e) => setShift({ ...shift, paramedic_id: e.target.value })}
                            className="fm-input"
                            data-testid="shift-paramedic"
                        >
                            <option value="">— Seleccionar —</option>
                            {paramedics.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="fm-label">Operador / Chofer</label>
                        <select
                            value={shift.driver_id}
                            onChange={(e) => setShift({ ...shift, driver_id: e.target.value })}
                            className="fm-input"
                            data-testid="shift-driver"
                        >
                            <option value="">— Seleccionar —</option>
                            {drivers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    {medics.length > 0 && (
                        <div>
                            <label className="fm-label">Médico (si aplica)</label>
                            <select
                                value={shift.medic_id}
                                onChange={(e) => setShift({ ...shift, medic_id: e.target.value })}
                                className="fm-input"
                                data-testid="shift-medic"
                            >
                                <option value="">— Sin médico —</option>
                                {medics.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="flex gap-2 pt-2">
                        <button onClick={save} className="fm-btn-accent flex-1 justify-center" data-testid="shift-save">
                            <Check className="w-3.5 h-3.5" /> Iniciar turno
                        </button>
                        {ready && <button onClick={clear} className="fm-btn-outline">Borrar</button>}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">
                        💡 Cuando rote el turno, abre este panel y cambia los nombres. Cabina ve quién está a bordo en tiempo real.
                    </div>
                </div>
            )}
        </div>
    );
}

export const getStoredShift = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
};
